<template>
  <side-modal
    :value="value"
    :eyebrow="$t('hedgeOS.dashboard.operationDrawer.eyebrow')"
    :title="$t('hedgeOS.dashboard.operationDrawer.title')"
    :description="$t('hedgeOS.dashboard.operationDrawer.description')"
    :footer-meta="$t('hedgeOS.dashboard.operationDrawer.footerMeta')"
    :secondary-label="$t('hedgeOS.dashboard.operationDrawer.cancel')"
    :primary-label="$t('hedgeOS.dashboard.operationDrawer.register')"
    class="register-operation-drawer"
    @input="$emit('input', $event)"
    @secondary="$emit('input', false)"
    @primary="$emit('primary')"
  >
    <form @submit.prevent>
      <div class="form-section">
        <h4>{{ $t('hedgeOS.dashboard.operationDrawer.sections.direction') }}</h4>
        <div class="seg">
          <input id="newop-dir-imp" v-model="direction" type="radio" value="import" />
          <label for="newop-dir-imp">{{ $t('hedgeOS.dashboard.operationDrawer.fields.import') }}</label>
          <input id="newop-dir-exp" v-model="direction" type="radio" value="export" />
          <label for="newop-dir-exp">{{ $t('hedgeOS.dashboard.operationDrawer.fields.export') }}</label>
          <input id="newop-dir-fin" v-model="direction" type="radio" value="financial" />
          <label for="newop-dir-fin">{{ $t('hedgeOS.dashboard.operationDrawer.fields.financial') }}</label>
        </div>
      </div>

      <div class="form-section">
        <h4>{{ $t('hedgeOS.dashboard.operationDrawer.sections.identification') }}</h4>
        <div class="form-grid">
          <div class="field full">
            <label>{{ $t('hedgeOS.dashboard.operationDrawer.fields.operationName') }} <span class="hint">{{ $t('hedgeOS.dashboard.operationDrawer.fields.operationNameHint') }}</span></label>
            <input v-model="operationName" type="text" :placeholder="$t('hedgeOS.dashboard.operationDrawer.fields.operationNamePlaceholder')" />
          </div>
          <div class="field">
            <label>{{ $t('hedgeOS.dashboard.operationDrawer.fields.counterparty') }} <span class="req">*</span></label>
            <input v-model="counterparty" type="text" :placeholder="$t('hedgeOS.dashboard.operationDrawer.fields.counterpartyPlaceholder')" />
          </div>
          <div class="field">
            <label>{{ $t('hedgeOS.dashboard.operationDrawer.fields.reference') }} <span class="hint">{{ $t('hedgeOS.dashboard.operationDrawer.fields.optionalHint') }}</span></label>
            <input v-model="reference" type="text" :placeholder="$t('hedgeOS.dashboard.operationDrawer.fields.referencePlaceholder')" />
          </div>
        </div>
      </div>

      <div class="form-section">
        <h4>
          {{ $t('hedgeOS.dashboard.operationDrawer.sections.riskFactors') }}
          <span class="section-note">{{ $t('hedgeOS.dashboard.operationDrawer.sections.selectAllThatApply') }}</span>
        </h4>
        <div class="rf-toggle">
          <input id="newop-rf-fx" v-model="riskFx" type="checkbox" />
          <label for="newop-rf-fx">{{ $t('hedgeOS.dashboard.operationDrawer.fields.fxCurrency') }}</label>
          <input id="newop-rf-cmd" v-model="riskCommodity" type="checkbox" />
          <label for="newop-rf-cmd">{{ $t('hedgeOS.dashboard.operationDrawer.fields.commodityPrice') }}</label>
        </div>

        <div class="conditional" :class="{ on: riskFx }">
          <div class="form-grid cols-3">
            <div class="field">
              <label>{{ $t('hedgeOS.dashboard.operationDrawer.fields.currency') }} <span class="req">*</span></label>
              <select v-model="currency">
                <option>USD</option>
                <option>EUR</option>
                <option>GBP</option>
                <option>JPY</option>
                <option>CNY</option>
              </select>
            </div>
            <div class="field">
              <label>{{ $t('hedgeOS.dashboard.operationDrawer.fields.notional') }} <span class="req">*</span></label>
              <input v-model.number="fxNotional" type="number" :placeholder="$t('hedgeOS.dashboard.operationDrawer.fields.notionalPlaceholder')" step="1000" />
            </div>
            <div class="field">
              <label>{{ $t('hedgeOS.dashboard.operationDrawer.fields.dealDateFx') }} <span class="hint">{{ $t('hedgeOS.dashboard.operationDrawer.fields.todayFx') }}</span></label>
              <input v-model.number="dealFx" type="number" :placeholder="$t('hedgeOS.dashboard.operationDrawer.fields.dealDateFxPlaceholder')" step="0.0001" />
            </div>
          </div>
        </div>

        <div class="conditional" :class="{ on: riskCommodity }">
          <div class="form-grid cols-3">
            <div class="field">
              <label>{{ $t('hedgeOS.dashboard.operationDrawer.fields.underlying') }} <span class="req">*</span></label>
              <select v-model="commodityUnderlying">
                <option value="lmeAluminum">{{ $t('hedgeOS.dashboard.operationDrawer.underlyings.lmeAluminum') }}</option>
                <option value="lmeCopper">{{ $t('hedgeOS.dashboard.operationDrawer.underlyings.lmeCopper') }}</option>
                <option value="cbotSoy">{{ $t('hedgeOS.dashboard.operationDrawer.underlyings.cbotSoy') }}</option>
                <option value="cbotCorn">{{ $t('hedgeOS.dashboard.operationDrawer.underlyings.cbotCorn') }}</option>
                <option value="cbotWheat">{{ $t('hedgeOS.dashboard.operationDrawer.underlyings.cbotWheat') }}</option>
                <option value="iceCoffee">{{ $t('hedgeOS.dashboard.operationDrawer.underlyings.iceCoffee') }}</option>
                <option value="iceBrent">{{ $t('hedgeOS.dashboard.operationDrawer.underlyings.iceBrent') }}</option>
                <option value="iceSugar">{{ $t('hedgeOS.dashboard.operationDrawer.underlyings.iceSugar') }}</option>
              </select>
            </div>
            <div class="field">
              <label>{{ $t('hedgeOS.dashboard.operationDrawer.fields.quantity') }} <span class="req">*</span></label>
              <input v-model.number="quantity" type="number" :placeholder="$t('hedgeOS.dashboard.operationDrawer.fields.quantityPlaceholder')" step="1" />
            </div>
            <div class="field">
              <label>{{ $t('hedgeOS.dashboard.operationDrawer.fields.unit') }}</label>
              <select v-model="unit">
                <option value="tonnes">{{ $t('hedgeOS.dashboard.operationDrawer.units.tonnes') }}</option>
                <option value="bushels">{{ $t('hedgeOS.dashboard.operationDrawer.units.bushels') }}</option>
                <option value="sacas">{{ $t('hedgeOS.dashboard.operationDrawer.units.sacas') }}</option>
                <option value="barrels">{{ $t('hedgeOS.dashboard.operationDrawer.units.barrels') }}</option>
                <option value="pounds">{{ $t('hedgeOS.dashboard.operationDrawer.units.pounds') }}</option>
              </select>
            </div>
            <div class="field full">
              <label>{{ $t('hedgeOS.dashboard.operationDrawer.fields.referencePriceAtDeal') }} <span class="hint">{{ $t('hedgeOS.dashboard.operationDrawer.fields.referencePriceHint') }}</span></label>
              <input v-model.number="refPrice" type="number" :placeholder="$t('hedgeOS.dashboard.operationDrawer.fields.referencePricePlaceholder')" step="0.01" />
            </div>
          </div>
        </div>
      </div>

      <div class="form-section">
        <h4>{{ $t('hedgeOS.dashboard.operationDrawer.sections.settlementDesignation') }}</h4>
        <div class="form-grid">
          <div class="field">
            <label>{{ $t('hedgeOS.dashboard.operationDrawer.fields.settlementDate') }} <span class="req">*</span></label>
            <input v-model="settlementDate" type="date" />
          </div>
          <div class="field">
            <label>{{ $t('hedgeOS.dashboard.operationDrawer.fields.tenor') }} <span class="hint">{{ $t('hedgeOS.dashboard.operationDrawer.fields.tenorHint') }}</span></label>
            <input type="text" :value="tenorLabel" readonly />
          </div>
          <div class="field full">
            <label>{{ $t('hedgeOS.dashboard.operationDrawer.fields.hedgeAccountingDesignation') }}</label>
            <div class="seg">
              <input id="newop-desig-cf" v-model="designation" type="radio" value="cash-flow" />
              <label for="newop-desig-cf">{{ $t('hedgeOS.dashboard.operationDrawer.fields.cashFlowHedge') }}</label>
              <input id="newop-desig-fv" v-model="designation" type="radio" value="fair-value" />
              <label for="newop-desig-fv">{{ $t('hedgeOS.dashboard.operationDrawer.fields.fairValueHedge') }}</label>
              <input id="newop-desig-econ" v-model="designation" type="radio" value="economic" />
              <label for="newop-desig-econ">{{ $t('hedgeOS.dashboard.operationDrawer.fields.economicOnly') }}</label>
            </div>
          </div>
        </div>
      </div>

      <div class="form-section">
        <h4>{{ $t('hedgeOS.dashboard.operationDrawer.sections.notes') }} <span class="section-note">{{ $t('hedgeOS.dashboard.operationDrawer.sections.optional') }}</span></h4>
        <div class="field full">
          <textarea v-model="notes" rows="3" :placeholder="$t('hedgeOS.dashboard.operationDrawer.fields.notesPlaceholder')"></textarea>
        </div>
      </div>
    </form>
  </side-modal>
</template>

<script>
import SideModal from "@/components/modal/SideModal.vue";

export default {
  name: "RegisterOperationDrawer",
  components: { SideModal },
  props: {
    value: Boolean,
  },
  data() {
    return {
      direction: "import",
      operationName: "",
      counterparty: "",
      reference: "",
      riskFx: true,
      riskCommodity: false,
      currency: "USD",
      fxNotional: null,
      dealFx: null,
      commodityUnderlying: "lmeAluminum",
      quantity: null,
      unit: "tonnes",
      refPrice: null,
      settlementDate: "2026-07-18",
      designation: "cash-flow",
      notes: "",
    };
  },
  computed: {
    tenorLabel() {
      if (!this.settlementDate) return "—";
      const msInDay = 86400000;
      const today = new Date();
      const target = new Date(`${this.settlementDate}T00:00:00`);
      const days = Math.max(0, Math.round((target - Date.UTC(today.getFullYear(), today.getMonth(), today.getDate())) / msInDay));
      return this.$t("hedgeOS.dashboard.operationDrawer.fields.tenorDays", { n: days });
    },
  },
};
</script>

<style scoped>
.form-section {
  margin-bottom: 2rem;
}

.form-section + .form-section {
  border-top: 1px solid var(--line);
  padding-top: 1.6rem;
}

.form-section h4 {
  font-family: "JetBrains Mono", monospace;
  font-size: 0.62rem;
  color: var(--muted);
  text-transform: uppercase;
  letter-spacing: 0.18em;
  margin-bottom: 1rem;
  font-weight: 400;
}

.form-section h4 .section-note {
  color: var(--dim);
  font-size: 0.55rem;
  margin-left: 0.4rem;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.1rem;
}

.form-grid.cols-3 {
  grid-template-columns: 1fr 1fr 1fr;
  margin-top: 1rem;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.field.full {
  grid-column: 1 / -1;
}

.field label {
  font-family: "JetBrains Mono", monospace;
  font-size: 0.6rem;
  color: var(--dim);
  text-transform: uppercase;
  letter-spacing: 0.15em;
  display: flex;
  justify-content: space-between;
}

.field label .req {
  color: var(--hot);
  font-size: 0.7rem;
}

.field label .hint {
  color: var(--muted);
  font-size: 0.55rem;
  text-transform: none;
  letter-spacing: 0.05em;
}

.field input[type="text"],
.field input[type="number"],
.field input[type="date"],
.field select,
.field textarea {
  background: var(--ink2, #131615);
  border: 1px solid var(--line);
  color: var(--paper);
  padding: 0.7rem 0.8rem;
  font-family: "JetBrains Mono", monospace;
  font-size: 0.85rem;
  transition: border-color 0.2s;
  width: 100%;
}

.field textarea {
  resize: vertical;
}

.field input:focus,
.field select:focus,
.field textarea:focus {
  outline: none;
  border-color: var(--hot);
}

.field input[readonly] {
  background: var(--ink3, #1c201e);
  color: var(--dim);
  cursor: not-allowed;
}

/* Segmented toggle control */
.seg {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(0, 1fr));
  border: 1px solid var(--line);
  background: var(--ink2, #131615);
}

.seg input {
  display: none;
}

.seg label {
  cursor: pointer;
  padding: 0.7rem 0.5rem;
  text-align: center;
  font-family: "JetBrains Mono", monospace;
  font-size: 0.7rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--dim);
  border-right: 1px solid var(--line);
  transition: all 0.2s;
  user-select: none;
  display: flex;
  align-items: center;
  justify-content: center;
}

.seg label:last-child {
  border-right: none;
}

.seg label:hover {
  background: var(--ink3, #1c201e);
  color: var(--paper);
}

.seg input:checked + label {
  background: var(--hot);
  color: var(--ink);
  font-weight: 600;
}

/* Risk factor toggle group */
.rf-toggle {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.rf-toggle input {
  display: none;
}

.rf-toggle label {
  cursor: pointer;
  padding: 0.55rem 0.9rem;
  border: 1px solid var(--line);
  background: var(--ink2, #131615);
  color: var(--dim);
  font-family: "JetBrains Mono", monospace;
  font-size: 0.72rem;
  letter-spacing: 0.08em;
  transition: all 0.2s;
  user-select: none;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.rf-toggle label::before {
  content: "";
  width: 8px;
  height: 8px;
  border: 1px solid currentColor;
  background: transparent;
}

.rf-toggle label:hover {
  border-color: var(--dim);
  color: var(--paper);
}

.rf-toggle input:checked + label {
  border-color: var(--hot);
  color: var(--hot);
  background: rgba(255, 77, 31, 0.08);
}

.rf-toggle input:checked + label::before {
  background: var(--hot);
  border-color: var(--hot);
}

/* Conditional show/hide */
.conditional {
  display: none;
  animation: slidein 0.3s ease;
  border-top: 1px dashed var(--line);
  padding-top: 1.4rem;
  margin-top: 1rem;
}

.conditional.on {
  display: block;
}

@keyframes slidein {
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 700px) {
  .form-grid,
  .form-grid.cols-3 {
    grid-template-columns: 1fr;
  }
}
</style>
